import React from 'react';
import { useTheme } from '@mui/material';
import dayjs from 'dayjs';
import DSDialog from '../../../design-system/DSDialog';
import DSDialogTitle from '../../../design-system/DSDialogTitle';
import DSDialogContent from '../../../design-system/DSDialogContent';
import DSBox from '../../../design-system/DSBox';
import DSCircularProgress from '../../../design-system/DSCircularProgress';
import DSTypography from '../../../design-system/Typography/DSTypography';
import useKlydosByDateQuery from '../../../hooks/useKlydosByDateQuery';
import { KLYDO_YELLOW } from '../../../consts/colors';
import KlydoCard from '../../Klydos/KlydosList/components/KlydoCard';
import { PopulatedKlydo } from '../../../api/klydo';
import DSGrid from '../../../design-system/DSGrid';

type CalendarDayDialogProps = {
  open: boolean;
  onClose: () => void;
  selectedDate: Date;
};

const CalendarDayDialog = ({
  open,
  onClose,
  selectedDate,
}: CalendarDayDialogProps) => {
  const { spacing, palette } = useTheme();

  const {
    data: klydos,
    isLoading,
    error,
  } = useKlydosByDateQuery({
    date: selectedDate,
    enabled: open,
  });

  const formattedDate = dayjs(selectedDate).format('MMMM D, YYYY');

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="xl">
      <DSDialogTitle>
        <DSTypography
          sx={{
            fontFamily: 'Gilroy-Bold',
            fontSize: '1.5rem',
            color: palette.text.primary,
          }}
        >
          Klydos for {formattedDate}
        </DSTypography>
      </DSDialogTitle>

      <DSDialogContent>
        {isLoading && (
          <DSBox
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <DSCircularProgress sx={{ color: KLYDO_YELLOW }} />
          </DSBox>
        )}

        {error && (
          <DSBox
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              padding: spacing(2),
            }}
          >
            <DSTypography
              sx={{
                color: palette.error.main,
                textAlign: 'center',
                fontFamily: 'Gilroy-Medium',
              }}
            >
              Failed to load klydos for this date. Please try again.
            </DSTypography>
          </DSBox>
        )}

        {!isLoading && !error && klydos && (
          <DSBox>
            {klydos.length === 0 ? (
              <DSBox
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '200px',
                  padding: spacing(2),
                }}
              >
                <DSTypography
                  sx={{
                    color: palette.text.secondary,
                    textAlign: 'center',
                    fontFamily: 'Gilroy-Medium',
                    fontSize: '1.1rem',
                  }}
                >
                  No klydos scheduled for this date.
                </DSTypography>
              </DSBox>
            ) : (
              <DSGrid
                container
                spacing={spacing(2)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  },
                }}
              >
                {klydos.map((klydo) => (
                  <KlydoCard
                    key={klydo.id}
                    klydo={klydo as PopulatedKlydo}
                    isMenuHidden
                    isStatic
                  />
                ))}
              </DSGrid>
            )}
          </DSBox>
        )}
      </DSDialogContent>
    </DSDialog>
  );
};

export default CalendarDayDialog;
